/* import __COLOCATED_TEMPLATE__ from './signup-add-project-form.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask, restartableTask, enqueueTask, timeout } from 'ember-concurrency';
import { array, object, string } from 'yup';
import { isEmpty } from '@ember/utils';
import { escapeRegExp ,isUrl } from 'sh-www/utils/misc';
import { workSampleUrlValidator } from 'sh-www/utils/validators';
import { stopClickEvent } from 'sh-www/utils/events';
import { validateSchema } from 'sh-www/utils/validators';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { IMAGE_COMMANDS__MODEL_TYPES } from 'sh-www/services/commands/image-commands';
import { CLASSIFICATION_CATEGORY_ORDER } from 'sh-www/models/classification';
import Validator from 'validator';
import {
  handleFormErrorOrModal,
} from 'sh-www/utils/handles-errors';
import clsx from 'clsx';

const SCHEMA = object({
  'title': string().required('You must provide a project title.'),
  'link': string()
    .required('You must provide a link.')
    .test(
      'link-requirement',
      'Link must be a valid URL',
      function(value) {
        const protocols = ['http', 'https'];
        return value ? Validator.isURL(value, { protocols }) : true;
      }
    ),
  'thumbnailImageSrc': string().required('A thumbnail must be provided.'),
  'description': string().required('You must provide a project description.')
    .max(500, 'Project description must be 500 characters or less.'),
  'services': array().required('Please select at least one service')
    .min(1, 'Please select at least one service'),
  'classifications': array().required('Please select at least one content tag')
    .min(1, 'Please select at least one content tag')
    .max(4, 'You may only have 4 or fewer content tags'),
});

const THUMBNAIL_FETCH_DEBOUNCE_MS = 300;
const THUMBNAIL_LOAD_ERROR = "We were unable to load a video thumbnail. This may indicate that the video is not visible to the public. If possible, review the video's privacy setting.";
const CLASSIFICATION_LIMIT = 4;

const CLASS_NAME = 'GLM-freelancer-signup-form';
const ADD_PROJECT_FORM_CLASS_NAME = `${CLASS_NAME}--add-project`;
const FORM_INPUTS = ['title', 'link', 'description', 'services', 'classifications'];

const categoryToClassificationSection = (category) => {
  return category.charAt(0).toUpperCase() + category.slice(1);
};

const sortAndGroupByCategory = (options) => {
  return options.sort((a, b) => {
    // first sort by category
    const categoryComparison = CLASSIFICATION_CATEGORY_ORDER[a.category] - CLASSIFICATION_CATEGORY_ORDER[b.category];
    if (categoryComparison !== 0) {
      return categoryComparison;
    }

    // then sort alphabetically
    return a.key.localeCompare(b.key);
  });
};

const mapClassificationModelsToOptions = (classifications) => {
  return classifications.map((classification) => ({
    key: classification.label,
    model: classification.id,
    category: categoryToClassificationSection(classification.category),
  }));
};

class SignupAddProjectForm extends Component {
  @service('commands.image-commands') imageCommandService;
  @service('commands.oembed-commands') oembedCommandService;
  @service('commands.projectdigest-commands') projectdigestCommandService;
  @service errorTracker;
  @service session;
  @service shData;
  @service store;

  @tracked title = this.args.projectdigest?.title ?? '';
  @tracked link = this.args.projectdigest?.link ?? '';
  @tracked description = this.args.projectdigest?.description ?? '';
  @tracked services = this.args.projectdigest?.services.toArray() ?? [];
  @tracked classifications = mapClassificationModelsToOptions(this.args.projectdigest?.classifications.toArray() ?? []);
  @tracked classificationsValue = '';

  @tracked uploadedThumbnail = null;
  @tracked uploadedThumbnailType = '';
  @tracked uploadedThumbnailAssociatedLink = '';
  @tracked fetchedThumbnail = this.args.projectdigest?.thumbnailUrl ?? '';

  @tracked fileInputRef = null;
  @tracked errors = {};

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
      ADD_PROJECT_FORM_CLASS_NAME
    );
  }

  get baseClassName() {
    return CLASS_NAME;
  }

  #sortedServicesCache = createCache(() => {
    const services = this.args.availableServices.slice();
    return services.sort((a, b) => {
      if (a.category === b.category) {
        return a.name.localeCompare(b.name);
      } else {
        return a.category.localeCompare(b.category);
      }
    });
  });

  get sortedServices() {
    return getValue(this.#sortedServicesCache);
  }

  #filteredClassificationOptionsCache = createCache(() => {
    const {
      classificationsValue,
      classificationOptions,
    } = this;

    if (!classificationsValue) {
      return classificationOptions;
    }

    const escapedInternalValue = escapeRegExp(classificationsValue);
    const pattern = new RegExp(`(\\b${escapedInternalValue})`, 'i');

    return classificationOptions.filter(({ key }) => {
      return pattern.test(key);
    });
  });

  get filteredClassificationOptions() {
    return getValue(this.#filteredClassificationOptionsCache);
  }

  get classificationIdToModelMap() {
    return this.classifications.reduce((accum, classification) => {
      accum[classification.model] = classification;
      return accum;
    }, {});
  }

  get classificationOptions() {
    const { classificationIdToModelMap } = this;

    const options = (this.args.availableClassifications ?? []).reduce((accum, classification) => {
      if (!classificationIdToModelMap[classification.id]) {
        accum.push(
          {
            key: classification.label,
            model: classification.id,
            category: categoryToClassificationSection(classification.category),
          }
        );
      }

      return accum;
    }, []);

    return sortAndGroupByCategory(options);
  }

  get isThumbnailPreviewLoading() {
    return this.onThumbnailChange.isRunning || this.updateThumbnailPreview.isRunning;
  }

  get uploadedThumbnailURL() {
    const { uploadedThumbnail } = this;

    if (!uploadedThumbnail) {
      return '';
    }

    const { cloudVersion, cloudId } = uploadedThumbnail.getProperties('cloudVersion', 'cloudId');

    return this.imageCommandService.cloudinaryThumbnailURL(cloudVersion, cloudId);
  }

  get thumbnailImageSrc() { 
    //if there is an uploaded thumbnail and its url differs from what is fetched (this.args.projectdigest?.thumbnailUrl or automatic link thumb)
    //then show uploaded thumbnail
    if (this.uploadedThumbnailURL && this.uploadedThumbnailURL !== this.fetchedThumbnail) {
      return this.uploadedThumbnailURL;
    }

    //if there's a new link, show a new thumbnail
    if (this.link !== this.args.projectdigest?.link) {
      return this.fetchedThumbnail;
    }

    //otherwise just show what we already have as a thumbnail
    return this.args.projectdigest?.thumbnailUrl;
  }

  get showThumbnail() {
    return this.isThumbnailPreviewLoading
    || Boolean(this.errors.uploadedThumbnail)
    || Boolean(this.thumbnailImageSrc && this.link)
    || Boolean(this.uploadedThumbnailURL);
  }

  get submitDisabled() {
    return this.submitTask.isRunning
      || this.isThumbnailPreviewLoading
      || Object.values(this.errors).some(error => !!error)
      || !this.title
      || !this.link
      || !this.description
      || !this.thumbnailImageSrc
      || this.services.length < 1
      || this.classifications.length < 1;
  }

  async reloadImage(projectDigest) {
    const response = await this.projectdigestCommandService.getProjectDigest(projectDigest.id, ['image']);
    this.shData.pushResponseToStore(response);
    return this.store.peekRecord('projectdigest', response.data.id);
  }

  getProjectData() {
    return {
      title: this.title,
      link: this.link,
      description: this.description,
      relationships: {
        services: {
          data: this.services.map(({ id }) => ({ id, type: 'services' })),
        },
      },
    };
  }

  @action
  validateForm(e) {
    e.preventDefault();
    const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
    this.errors = validationErrors;

    if (Object.values(this.errors).every(error => !error)) {
      this.submitTask.perform();
    }
  }

  @action
  handleInput(field, value) {
    this[field] = value;
    this.errors = { ...this.errors, [field]: undefined };

    if (field === 'link') {
      this.updateThumbnailPreview.perform(value);
    }
  }

  @action
  handleServiceAdded(addedService) {
    this.services = [ ...this.services, addedService ]
    this.errors = { ...this.errors, services: undefined };
  }

  @action
  handleServiceRemoved(removedService) {
    this.services = this.services.filter(service => service !== removedService);
    this.errors = { ...this.errors, services: undefined };
  }

  @action
  onChangeThumbnailClick(event) {
    stopClickEvent(event);
    this.fileInputRef?.click();
  }

  @action
  onThumbnailLoadError() {
    this.errors = { ...this.errors, thumbnailImageSrc: THUMBNAIL_LOAD_ERROR };
  }

  @action
  setFileInputRef(ref) {
    this.fileInputRef = ref;
  }

  @action
  handleClassificationBlur() {
    this.classificationsValue = '';
  }

  @action
  handleClassificationInput(value) {
    this.classificationsValue = value;
  }

  @action
  handleClassificationRemoved(removedClassification) {
    this.classifications = this.classifications.filter((classification) => classification !== removedClassification);
  }

  @action
  handleClassificationAdded(addedClassification) {
    const currentClassifications = this.classifications;
    if (currentClassifications.length < CLASSIFICATION_LIMIT) {
      this.classifications = [ ...currentClassifications, addedClassification ];
    }

    this.classificationsValue = '';
    this.errors = { ...this.errors, classifications: undefined };
  }

  @dropTask
  *onThumbnailChange(event) {
    const [file] = event.target.files;
    event.target.value = '';
    this.errors = { ...this.errors, thumbnailImageSrc: undefined };

    try {
      const image = yield this.imageCommandService.createDraftImage(file);
      this.uploadedThumbnail = image;
      this.uploadedThumbnailType = file.type;
      this.uploadedThumbnailAssociatedLink = this.link;
    } catch (error) {
      this.errorTracker.error(error);
      this.errors = { ...this.errors, thumbnailImageSrc: 'There was an error uploading your thumbnail. Please try again' };
    }
  }

  @restartableTask
  *updateThumbnailPreview(url) {
    if (!isUrl(url)) return;
    if (workSampleUrlValidator(url)) return;

    this.errors = { ...this.errors, thumbnailImageSrc: undefined };

    yield timeout(THUMBNAIL_FETCH_DEBOUNCE_MS);

    try {
      const thumbnailUrl = yield this.oembedCommandService.getThumbnail(url, true);
      this.fetchedThumbnail = thumbnailUrl;
    } catch (e) {
      this.errors = { ...this.errors, thumbnailImageSrc: THUMBNAIL_LOAD_ERROR };
    }
  }

  @enqueueTask
  *saveCustomThumbnail(projectDigest, thumbnail) {
    if (!thumbnail) return projectDigest;

    const params = [
      IMAGE_COMMANDS__MODEL_TYPES.PROJECT_DIGEST,
      projectDigest.id,
      thumbnail.width,
      thumbnail.height,
      thumbnail.cloudId,
      thumbnail.cloudVersion,
      this.uploadedThumbnailType,
    ];

    yield this.imageCommandService.saveNewImageForModel(...params);
    return projectDigest;
  }

  @dropTask
  *createProject(user, thumbnail, data, classifications) {
    try {
      const projectDigest = yield this.projectdigestCommandService.createProjectDigest(user, data);
      yield this.saveCustomThumbnail.perform(projectDigest, thumbnail);
      yield this.reloadImage(projectDigest);

      const newClassificationIds = classifications.map(({ model }) => model);
      yield this.projectdigestCommandService.updateProjectdigestClassifications(projectDigest.id, newClassificationIds);
      const projectDigestWithClassifications = yield this.projectdigestCommandService.getProjectDigest(projectDigest.id, ['classifications']);
      this.shData.pushResponseToStore(projectDigestWithClassifications);
    } catch (error) {
      handleFormErrorOrModal(
        this,
        error,
        FORM_INPUTS,
        false,
        'There was an error creating this work sample. Please refresh and try again.',
        'createProject -- error creating project digest.' + JSON.stringify(error)
      );
      throw error;
    }
  }

  @dropTask
  *updateProject(user, thumbnail, data, classifications, projectdigest) {
    try {
      const currentClassificationIds = projectdigest.classifications.map(({ id }) => id);
      const newClassificationIds = classifications.map(({ model }) => model);
      const areClassificationsEqual = (arr1, arr2) => {
        return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
      };

      // update classifications if anything changed
      yield (
        areClassificationsEqual(currentClassificationIds, newClassificationIds)
          ? Promise.resolve()
          : this.projectdigestCommandService.updateProjectdigestClassifications(projectdigest.id, newClassificationIds)
      );

      const projectDigest = yield this.projectdigestCommandService.updateProjectDigest(user, projectdigest, data);
      yield this.saveCustomThumbnail.perform(projectDigest, thumbnail);
      yield this.reloadImage(projectDigest);
    } catch (error) {
      handleFormErrorOrModal(
        this,
        error,
        FORM_INPUTS,
        false,
        'There was an error creating this work sample. Please refresh and try again.',
        'updateProject -- error updating project digest.' + JSON.stringify(error)
      );
      throw error;
    }
  }

  @dropTask
  *submitTask() {
    const { projectdigest } = this.args;
    const { currentUser } = this.session;
    const { uploadedThumbnail, classifications } = this;
    const data = this.getProjectData();

    try {
      !isEmpty(projectdigest)
        ? yield this.updateProject.perform(currentUser, uploadedThumbnail, data, classifications, projectdigest)
        : yield this.createProject.perform(currentUser, uploadedThumbnail, data, classifications);

      this.args.exitProjectForm();
    } catch(error) {
      //Do nothing. We just want to abort before exiting the form.
    }    
  }
}

export default SignupAddProjectForm;
